import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clubsService } from '../../../services';
import StandClayConfigFields from './StandClayConfigFields';
import {
    buildStandClayConfig,
    buildStandType,
    buildPresentationDefaults,
    extractPresentationDefaults,
    disciplineTypes,
    formatStandIdentity,
    extractTargetLabels,
    getDefaultTargetLabelsForDiscipline,
    parseStandIdentity
} from './standClayConfig';
import '../Clubs/ClubsDashboard.css';
import './StandSetup.css';

const EditStand = () => {
    const { clubId, standId } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [photoDescription, setPhotoDescription] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [videoDescription, setVideoDescription] = useState('');
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [isSavingVideo, setIsSavingVideo] = useState(false);
    const [targetLabels, setTargetLabels] = useState([]);
    const [presentationDefaults, setPresentationDefaults] = useState({});
    const [customTargetInput, setCustomTargetInput] = useState('');
    const [standNumber, setStandNumber] = useState('');
    const [standTitle, setStandTitle] = useState('');

    useEffect(() => {
        const fetchStand = async () => {
            try {
                const data = await clubsService.getStand(standId);
                setFormData(data);
                const identity = parseStandIdentity(data?.name);
                setStandNumber(identity.standNumber);
                setStandTitle(identity.standTitle);
                const savedTargetLabels = extractTargetLabels(data?.clayConfigJson);
                const resolvedTargetLabels = savedTargetLabels.length > 0
                    ? savedTargetLabels
                    : getDefaultTargetLabelsForDiscipline(data?.discipline);
                setTargetLabels(resolvedTargetLabels);
                setPresentationDefaults(
                    buildPresentationDefaults(resolvedTargetLabels, extractPresentationDefaults(data?.clayConfigJson))
                );
            } catch (err) {
                console.error(err);
                setError('Failed to load stand details.');
                if (window.toast) {
                    window.toast.error('Failed to load stand details.');
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchStand();
    }, [standId]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'discipline') {
            setFormData((current) => ({
                ...current,
                discipline: value,
                variant: '',
                standType: buildStandType(value, '')
            }));
            const nextLabels = getDefaultTargetLabelsForDiscipline(value);
            setTargetLabels(nextLabels);
            setPresentationDefaults(buildPresentationDefaults(nextLabels));
            return;
        }

        if (name === 'variant') {
            setFormData((current) => ({
                ...current,
                variant: value,
                standType: buildStandType(current.discipline, value)
            }));
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    const refreshStand = async () => {
        const data = await clubsService.getStand(standId);
        setFormData(data);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const displayStandName = formatStandIdentity(standNumber, standTitle, formData.name);

        if (targetLabels.length === 0) {
            if (window.toast) {
                window.toast.error('Add at least one clay label for this stand.');
            }
            return;
        }

        try {
            await clubsService.updateStand(standId, {
                ...formData,
                name: displayStandName,
                standType: buildStandType(formData.discipline, formData.variant),
                clayConfigJson: buildStandClayConfig({
                    standName: displayStandName,
                    discipline: formData.discipline,
                    variant: formData.variant,
                    targetLabels,
                    presentationDefaults
                }),
                latitude: formData.latitude ? parseFloat(formData.latitude) : 0,
                longitude: formData.longitude ? parseFloat(formData.longitude) : 0
            });

            if (window.toast) {
                window.toast.success('Stand updated successfully!');
            }
            navigate(`/dashboard/club/${clubId}/stands`);
        } catch (err) {
            console.error('Error updating stand:', err);
            if (window.toast) {
                window.toast.error(`Failed to update stand: ${err.message}`);
            }
        }
    };

    const handlePhotoUpload = async () => {
        if (!photoFile) {
            return;
        }

        setIsUploadingPhoto(true);
        try {
            const formDataPayload = new FormData();
            formDataPayload.append('file', photoFile);
            formDataPayload.append('description', photoDescription);
            await clubsService.uploadStandPhoto(standId, formDataPayload);
            await refreshStand();
            setPhotoFile(null);
            setPhotoDescription('');
            if (window.toast) {
                window.toast.success('Stand photo uploaded.');
            }
        } catch (err) {
            console.error('Error uploading stand photo:', err);
            if (window.toast) {
                window.toast.error(err.message || 'Failed to upload stand photo.');
            }
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const handleDeletePhoto = async (photoId) => {
        try {
            await clubsService.deleteStandPhoto(standId, photoId);
            await refreshStand();
        } catch (err) {
            console.error('Error deleting stand photo:', err);
            if (window.toast) {
                window.toast.error(err.message || 'Failed to delete stand photo.');
            }
        }
    };

    const handleAddVideo = async () => {
        if (!videoUrl.trim()) {
            return;
        }

        setIsSavingVideo(true);
        try {
            await clubsService.addStandVideo(standId, {
                videoUrl: videoUrl.trim(),
                description: videoDescription.trim()
            });
            await refreshStand();
            setVideoUrl('');
            setVideoDescription('');
            if (window.toast) {
                window.toast.success('Stand video saved.');
            }
        } catch (err) {
            console.error('Error saving stand video:', err);
            if (window.toast) {
                window.toast.error(err.message || 'Failed to save stand video.');
            }
        } finally {
            setIsSavingVideo(false);
        }
    };

    const handleDeleteVideo = async (videoId) => {
        try {
            await clubsService.deleteStandVideo(standId, videoId);
            await refreshStand();
        } catch (err) {
            console.error('Error deleting stand video:', err);
            if (window.toast) {
                window.toast.error(err.message || 'Failed to delete stand video.');
            }
        }
    };

    const handleCancel = () => {
        navigate(`/dashboard/club/${clubId}/stands`);
    };

    const displayStandName = formatStandIdentity(standNumber, standTitle, formData?.name);

    if (isLoading || !formData) {
        return <div className="loading-container"><div className="loading-spinner"></div></div>;
    }

    return (
        <div className="dashboard-component">
            <h1 className="mb-4">Edit Stand</h1>

            {error && (
                <div className="error-message" role="alert">
                    <p>{error}</p>
                </div>
            )}

            <div className="dashboard-card p-4">
                <form className="dashboard-form" onSubmit={handleSubmit}>
                    <div className="dashboard-form-grid">
                        <div className="form-group">
                            <label>Stand Number</label>
                            <input
                                type="number"
                                min="1"
                                value={standNumber}
                                onChange={(e) => setStandNumber(e.target.value)}
                                placeholder="4"
                            />
                            <p className="form-helper-text">Optional. Keep this aligned with the number on your printed course map or stand QR code when you use one.</p>
                        </div>

                        <div className="form-group">
                            <label>Stand Name / Title</label>
                            <input
                                type="text"
                                value={standTitle}
                                onChange={(e) => setStandTitle(e.target.value)}
                                placeholder="Rabbits, English Skeet, Left / Right Quarter Away"
                                required
                            />
                            <p className="form-helper-text">Required. This can be a nickname, layout name, or presentation title if the stand is not numbered.</p>
                        </div>

                        <div className="form-group col-span-2">
                            <label>Presentation Summary / Tips</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="E.g., Pair of quick rabbits left to right then right to left"
                            />
                            <p className="form-helper-text">Use this for a brief summary of the birds, flightline, or any coaching note worth showing on the stand page.</p>
                        </div>

                        <div className="form-group col-span-2">
                            <div className="stand-identity-preview">
                                <span className="stand-identity-preview-label">Preview</span>
                                <strong>{displayStandName || 'Stand name will appear here'}</strong>
                                <p>This is how the stand will appear back on the venue stand list and in quick fun shoot selection.</p>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Discipline</label>
                            <select
                                name="discipline"
                                value={formData.discipline}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Discipline</option>
                                {Object.keys(disciplineTypes).map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>

                        {formData.discipline && (
                            <div className="form-group">
                                <label>Variant</label>
                                <select
                                    name="variant"
                                    value={formData.variant}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Variant</option>
                                    {disciplineTypes[formData.discipline].map((v) => (
                                        <option key={v} value={v}>{v}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {formData.discipline && (
                            <StandClayConfigFields
                                discipline={formData.discipline}
                                targetLabels={targetLabels}
                                presentationDefaults={presentationDefaults}
                                customTargetInput={customTargetInput}
                                onCustomTargetInputChange={setCustomTargetInput}
                                onTargetLabelsChange={setTargetLabels}
                                onPresentationDefaultsChange={setPresentationDefaults}
                            />
                        )}
                    </div>

                    <div className="form-buttons mt-4">
                        <button
                            type="submit"
                            className="dashboard-card-default-btn me-3"
                        >
                            Save Changes
                        </button>
                        <button
                            type="button"
                            className="dashboard-card-outline-btn"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            <div className="dashboard-card p-4 mt-4">
                <h2 className="mb-3">Stand Photos</h2>
                <p className="text-muted">Upload flightpaths, trap views, or signage so shooters can see the stand setup clearly.</p>
                <div className="dashboard-form-grid">
                    <div className="form-group">
                        <label>Photo file</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <input
                            type="text"
                            value={photoDescription}
                            onChange={(e) => setPhotoDescription(e.target.value)}
                            placeholder="Crossing pair from left trap"
                        />
                    </div>
                </div>
                <div className="form-buttons mt-3">
                    <button
                        type="button"
                        className="dashboard-card-default-btn"
                        onClick={handlePhotoUpload}
                        disabled={!photoFile || isUploadingPhoto}
                    >
                        {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </button>
                </div>

                <div className="row mt-4">
                    {(formData.photos || []).length === 0 ? (
                        <div className="col-12"><p className="text-muted mb-0">No stand photos yet.</p></div>
                    ) : (formData.photos || []).map((photo) => (
                        <div key={photo.photoId} className="col-md-4 mb-3">
                            <div className="dashboard-card p-2 h-100">
                                <img src={photo.photoUrl} alt={photo.description || formData.name} style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '12px' }} />
                                <div className="mt-2">
                                    <p className="mb-2">{photo.description || 'No description'}</p>
                                    <button type="button" className="dashboard-card-outline-btn" onClick={() => handleDeletePhoto(photo.photoId)}>
                                        Remove Photo
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="dashboard-card p-4 mt-4">
                <h2 className="mb-3">Stand Videos</h2>
                <p className="text-muted">Add a YouTube, Vimeo, or hosted video URL to show target lines, safety notes, or a walkthrough.</p>
                <div className="dashboard-form-grid">
                    <div className="form-group">
                        <label>Video URL</label>
                        <input
                            type="url"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            placeholder="https://..."
                        />
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <input
                            type="text"
                            value={videoDescription}
                            onChange={(e) => setVideoDescription(e.target.value)}
                            placeholder="Full stand walkthrough"
                        />
                    </div>
                </div>
                <div className="form-buttons mt-3">
                    <button
                        type="button"
                        className="dashboard-card-default-btn"
                        onClick={handleAddVideo}
                        disabled={!videoUrl.trim() || isSavingVideo}
                    >
                        {isSavingVideo ? 'Saving...' : 'Add Video'}
                    </button>
                </div>

                <div className="mt-4">
                    {(formData.videos || []).length === 0 ? (
                        <p className="text-muted mb-0">No stand videos yet.</p>
                    ) : (formData.videos || []).map((video) => (
                        <div key={video.videoId} className="dashboard-card p-3 mb-3">
                            <p className="mb-1"><strong>{video.description || 'Stand video'}</strong></p>
                            <a href={video.videoUrl} target="_blank" rel="noreferrer">{video.videoUrl}</a>
                            <div className="mt-3">
                                <button type="button" className="dashboard-card-outline-btn" onClick={() => handleDeleteVideo(video.videoId)}>
                                    Remove Video
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default EditStand;
